import { useTheme } from '@emotion/react';
import Form, { useForm } from 'rc-field-form';
import React from 'react';

import { CategoryExtensionProps } from '~src/category/category-manager';
import { OpenModalButton } from '~src/commons/components/button/open-modal-button';
import { FormLayout } from '~src/commons/components/layout/form-layout';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { fontStyles } from '~src/theme/fontStyles';

import { AdditionalInfo } from '../../api/api';
import { FullscreenComponent } from '../fullscreen-internals/fullscreen-mode';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { DialogLayout } from '../ui/dialog-layout';
import { Field } from '../ui/field';
import { TextInput } from '../ui/text-input';
import { useGlobalState, useSessionState } from './../global-state';
import { CustomerNumberProvider } from './customer-number-provider';

interface CustomerNumberModalProps {
  onClose: () => void;
  onSubmit: (values: AdditionalInfo) => void;
}

const CustomerNumberInfo: React.FC = () => {
  const { tICU } = useTranslations();

  return (
    <div
      css={{
        marginBottom: '24px',
        ...fontStyles.px12_18,
      }}
    >
      {tICU('widget.CUSTOMER_NUMBER_INFO')}
      &nbsp;
      <a
        target="_blank"
        rel="noreferrer"
        href={tICU('widget.ADD_INFO_MODAL.CUSTOMER_NUMBER_INFO_SITE')}
      >
        {tICU('widget.WEBSITE')}
      </a>
    </div>
  );
};

export const CustomerNumberModal: FullscreenComponent<CustomerNumberModalProps> = ({
  onClose,
  onSubmit,
}) => {
  const state = useGlobalState();

  const [formInstance] = useForm();

  const { tICU } = useTranslations();

  return (
    <Dialog width={520} onClose={onClose}>
      <DialogLayout title={tICU('widget.CUSTOMER_NUMBER_HEADER')} onClose={onClose}>
        <Form
          css={{
            height: '100%',
          }}
          initialValues={state.additional_info}
          form={formInstance}
          onFinish={onSubmit}
        >
          <FormLayout
            submitButton={
              <Button css={{ marginTop: '30px' }} type="submit">
                {tICU('widget.ADDRESS_MODAL.SAVE_BTN')}
              </Button>
            }
          >
            <CustomerNumberInfo />
            <Field
              name="customer_number"
              rules={[
                {
                  required: true,
                  message: tICU('widget.CUSTOMER_NUMBER_REQUIRED_VALIDATION'),
                },
                {
                  message: tICU('widget.CUSTOMER_NUMBER_VALIDATION'),
                  pattern: /^[0-9]{8,10}$/,
                },
              ]}
            >
              {(props, meta) => (
                <TextInput
                  {...props}
                  label={tICU('widget.CUSTOMER_NUMBER_PLACEHOLDER')}
                  error={meta.errors[0]}
                  name="customer_number"
                />
              )}
            </Field>
          </FormLayout>
        </Form>
      </DialogLayout>
    </Dialog>
  );
};
CustomerNumberModal.fullscreenName = 'CustomerNumberModal';

export const CustomerNumberExtension: React.FC<CategoryExtensionProps> = ({
  selected,
  selectedOption,
}) => {
  const globalState = useGlobalState();
  const session = useSessionState();
  const { tICU } = useTranslations();
  const theme = useTheme();

  if (!selected || !selectedOption.flags.show_customer_number) {
    return null;
  }

  const text =
    globalState.additional_info?.customer_number || tICU('widget.CUSTOMER_NUMBER_PLACEHOLDER');

  return (
    <CustomerNumberProvider>
      {({ onOpen }) => (
        <OpenModalButton
          onClick={onOpen}
          title={{ text, color: theme.black }}
          ariaLabel={tICU('widget.CUSTOMER_NUMBER_PLACEHOLDER')}
          alterIcon={session.flags.enable_inline_category_extensions}
        />
      )}
    </CustomerNumberProvider>
  );
};
